import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DataProviderInstance } from '../../api/DataProvider';
import { canAccess } from '../../constants/userRoles';
import { GetFirstStepsAPI, SeeFirstStepAPI } from '../../api/API';
import TOUR_SCRIPTS, { EXAMPLE_TOUR } from './tourScripts';
import { isDemoRecord } from '../demo-data/demoData';

const GuidedTourContext = createContext(null);

// Enquanto o guia está ligado, confere no servidor se o passo já foi feito
const POLL_MS = 5000;

/** O roteiro já começa na tela atual (ex.: o pagamento na tela do atendimento que acabou de ser registrado). */
const startsHere = (script, pathname) => (script.startHere || []).some((prefix) => pathname.startsWith(prefix));

const isShowcase = (key) => Boolean(key && TOUR_SCRIPTS[key]?.showcase);

const nextPending = (items, skipKey) =>
  items.find((item) => !item.done && item.key !== skipKey && TOUR_SCRIPTS[item.key]) || null;

/**
 * Guia na tela dos Primeiros passos. O "feito" vem do servidor (FirstSteps.php),
 * o passo em andamento fica guardado por pessoa e por clínica neste navegador,
 * para o guia continuar depois de recarregar ou trocar de tela.
 *
 * O guia "Conheça sua conta" (EXAMPLE_TOUR, showcase) usa o mesmo balão, mas é de
 * ver: a pessoa avança pelo Próximo (advance) e a parada atual fica guardada junto
 * com a chave ("example:2"), sem nada conferido no servidor.
 */
export function GuidedTourProvider({ children }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;
  const { UserData, session, CompanyConfig } = DataProviderInstance();
  const companyId = session?.currentCompany;
  const userId = UserData?.id;
  // O guia acompanha a configuração da clínica: só proprietário(a) e administrador(a)
  // (papeis.md, G3). Para a Equipe, nada é pedido ao servidor.
  const role = session?.companies?.find((company) => company?.id == companyId)?.role?.name;
  const canSetUp = canAccess(role, 'firstSteps');
  const storageKey = companyId != null && userId != null ? `gabriela.guidedTour.${companyId}.${userId}` : null;

  const [activeKey, setActiveKey] = useState(null);
  const [finishedKey, setFinishedKey] = useState(null);
  // Item que acabou de começar: mostra a apresentação antes do primeiro destaque
  const [introKey, setIntroKey] = useState(null);
  const [items, setItems] = useState([]);
  const [demoPatientId, setDemoPatientId] = useState(null);
  const [showcaseIndex, setShowcaseIndex] = useState(0);
  const demoData = CompanyConfig?.DemoData || null;

  /** Rota de uma parada do guia de ver (null: a parada não vale para esta conta). */
  const showcasePath = useCallback((step) => (typeof step.path === 'function' ? step.path(demoData) : step.path), [demoData]);

  /** Primeira parada a partir de index que vale para esta conta, ou -1. */
  const showcaseFrom = useCallback((index) => {
    const steps = TOUR_SCRIPTS[EXAMPLE_TOUR].steps;
    for (let position = index; position < steps.length; position += 1) {
      if (showcasePath(steps[position])) return position;
    }
    return -1;
  }, [showcasePath]);

  const persist = useCallback((value) => {
    if (!storageKey) return;
    if (value) localStorage.setItem(storageKey, value);
    else localStorage.removeItem(storageKey);
  }, [storageKey]);

  // Uma consulta ao servidor por vez: quem pede enquanto outra está em andamento
  // recebe a mesma resposta, e a conferência periódica pula a vez se acabou de consultar
  const pending = useRef(null);
  const lastFetchAt = useRef(0);

  const refresh = useCallback((onLoaded) => {
    if (!canSetUp) {
      setItems([]);
      if (onLoaded) onLoaded([]);
      return;
    }
    if (pending.current) {
      if (onLoaded) pending.current.push(onLoaded);
      return;
    }
    const callbacks = onLoaded ? [onLoaded] : [];
    pending.current = callbacks;
    lastFetchAt.current = Date.now();
    GetFirstStepsAPI((response) => {
      pending.current = null;
      if (!response?.success) return;
      setItems(response.items || []);
      setDemoPatientId(response.demoPatientId ?? null);
      callbacks.forEach((callback) => callback(response.items || []));
    });
  }, [canSetUp]);

  const stop = useCallback(() => {
    setActiveKey(null);
    setFinishedKey(null);
    setIntroKey(null);
    persist(null);
  }, [persist]);

  /** Começa pelo item pedido ou, sem chave, pelo primeiro que ainda falta. */
  const start = useCallback((itemKey) => {
    if (isShowcase(itemKey)) {
      const index = showcaseFrom(0);
      if (index < 0) {
        stop();
        navigate('/dashboard');
        return;
      }
      setFinishedKey(null);
      setActiveKey(itemKey);
      setIntroKey(itemKey);
      setShowcaseIndex(index);
      persist(`${itemKey}:${index}`);
      navigate(showcasePath(TOUR_SCRIPTS[itemKey].steps[index]));
      return;
    }
    refresh((list) => {
      if (list.some((entry) => entry.key === 'setup')) {
        stop();
        navigate('/setup-wizard');
        return;
      }
      const item = itemKey ? list.find((entry) => entry.key === itemKey) : nextPending(list);
      if (!item || !TOUR_SCRIPTS[item.key]) {
        stop();
        navigate('/dashboard');
        return;
      }
      setFinishedKey(null);
      setActiveKey(item.key);
      setIntroKey(item.key);
      persist(item.key);
      const script = TOUR_SCRIPTS[item.key];
      if (!startsHere(script, pathnameRef.current)) navigate(script.path);
    });
  }, [refresh, stop, persist, navigate, showcaseFrom, showcasePath]);

  /** Guia de ver: vai para a próxima parada; depois da última, encerra onde está. */
  const advance = useCallback(() => {
    if (!isShowcase(activeKey)) return;
    const index = showcaseFrom(showcaseIndex + 1);
    if (index < 0) {
      stop();
      return;
    }
    setShowcaseIndex(index);
    persist(`${activeKey}:${index}`);
    navigate(showcasePath(TOUR_SCRIPTS[activeKey].steps[index]));
  }, [activeKey, showcaseIndex, showcaseFrom, showcasePath, stop, persist, navigate]);

  // Retoma o guia de onde a pessoa parou
  useEffect(() => {
    if (!storageKey) return;
    const [savedKey, savedIndex] = (localStorage.getItem(storageKey) || '').split(':');
    setActiveKey(savedKey && TOUR_SCRIPTS[savedKey] ? savedKey : null);
    setShowcaseIndex(Number(savedIndex) || 0);
    setFinishedKey(null);
  }, [storageKey]);

  /** Pergunta ao servidor se o passo em andamento já foi feito. */
  const check = useCallback(() => {
    if (!activeKey || finishedKey || isShowcase(activeKey)) return;
    refresh((list) => {
      const item = list.find((entry) => entry.key === activeKey);
      // O guia só existe depois da configuração inicial (o item "setup" some ao concluir)
      if (!item || list.some((entry) => entry.key === 'setup')) stop();
      else if (item.done) setFinishedKey(activeKey);
    });
  }, [activeKey, finishedKey, refresh, stop]);

  /** Itens que ficam feitos só de ver a tela (estatísticas): grava a visita e confere. */
  const markSeen = useCallback(() => {
    if (!activeKey) return;
    SeeFirstStepAPI(activeKey, () => check());
  }, [activeKey, check]);

  useEffect(() => {
    if (!activeKey || finishedKey || isShowcase(activeKey)) return undefined;
    // Pula a vez quando outra conferência (salvamento, vídeo, efeito repetido) acabou de consultar
    const poll = () => {
      if (Date.now() - lastFetchAt.current >= POLL_MS - 1000) check();
    };
    poll();
    const timer = setInterval(poll, POLL_MS);
    return () => clearInterval(timer);
  }, [activeKey, finishedKey, check]);

  // Parada atual do guia de ver, com a rota já resolvida
  const showcase = useMemo(() => {
    if (!isShowcase(activeKey)) return null;
    const steps = TOUR_SCRIPTS[activeKey].steps;
    const step = steps[showcaseIndex];
    const path = step ? showcasePath(step) : null;
    if (!path) return null;
    return { step: { ...step, path }, index: showcaseIndex, total: steps.length };
  }, [activeKey, showcaseIndex, showcasePath]);

  const value = useMemo(() => ({
    activeKey,
    finishedKey,
    item: items.find((entry) => entry.key === activeKey) || null,
    next: finishedKey ? nextPending(items, finishedKey) : null,
    items,
    intro: introKey !== null && introKey === activeKey,
    closeIntro: () => setIntroKey(null),
    demoPatientId,
    showcase,
    start,
    advance,
    stop,
    check,
    markSeen,
  }), [activeKey, finishedKey, introKey, items, demoPatientId, showcase, start, advance, stop, check, markSeen]);

  return <GuidedTourContext.Provider value={value}>{children}</GuidedTourContext.Provider>;
}

/** Fora do provider (telas públicas) devolve um guia sempre desligado. */
export const useGuidedTour = () => useContext(GuidedTourContext) || {
  activeKey: null, finishedKey: null, item: null, next: null, items: [], intro: false, closeIntro: () => {},
  demoPatientId: null, showcase: null, start: () => {}, advance: () => {}, stop: () => {}, check: () => {}, markSeen: () => {},
};

// Status em que o agendamento ainda pode ser atendido (mesma lista do botão Atender)
const ATTENDABLE = ['scheduled', 'confirmed', 'confirmed_by_patient', 'waiting', 'doing', 'rescheduled'];

const isInvoiced = (appointment) => Boolean(appointment.invoiceId) && String(appointment.invoiceId) !== '0';

// Card da agenda que cada item do guia destaca
const AGENDA_CARD = {
  attend: (appointment) => ATTENDABLE.includes(appointment.status),
  payment: (appointment) => appointment.status === 'completed' && !isInvoiced(appointment),
};

/**
 * Marca para os guias que começam num card da agenda ("Atenda seu primeiro paciente"
 * e "Registre o primeiro pagamento"): devolve o data-tour do card que serve ao item,
 * fora o paciente de teste da configuração e os dados de exemplo (is_demo).
 */
export const useAttendTourTarget = () => {
  const { activeKey, finishedKey, demoPatientId } = useGuidedTour();
  return (appointment) => {
    const matches = AGENDA_CARD[activeKey];
    if (!matches || finishedKey || !appointment || !matches(appointment)) return undefined;
    if (demoPatientId != null && String(appointment.patient) === String(demoPatientId)) return undefined;
    if (isDemoRecord(appointment)) return undefined;
    return 'agenda-appointment';
  };
};
